import { Grid, styled } from '@mui/material';
import { memo } from 'react';
import SizeDisplay from '../../../Common/Measurements/SizeDisplay';

const ShapeGroupGridItem = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
});

const ShapeGroupGrid = styled(Grid)({
  maxWidth: 320,
  width: '100%',
});

const Diagram = styled('svg')({
  display: 'block',
  height: 'auto',
  margin: '0 auto',
  maxWidth: 320,
  overflow: 'visible',
  width: '100%',
});

type PatchShapeType = 'oval' | 'rectangle' | 'square' | 'circle';

interface PatchShape {
  height: number
  heightInches?: number
  id: string
  width: number
  widthInches: number
}

interface PatchShapeGroup {
  id: string
  type: PatchShapeType
  variants: PatchShape[]
}

const diagramWidth = 350;
const labeledShapeAreaWidth = 240;
const diagramTopPadding = 68;
const diagramBottomPadding = 12;

const ovalPatchShapes: PatchShape[] = [
  { id: 'oval-large', width: 220, height: 157, widthInches: 3.5, heightInches: 2.5 },
  { id: 'oval-small', width: 173, height: 126, widthInches: 2.75, heightInches: 2 },
];

const rectanglePatchShapes: PatchShape[] = [
  { id: 'rectangle-large', width: 220, height: 157, widthInches: 3.5, heightInches: 2.5 },
  { id: 'rectangle-small', width: 173, height: 126, widthInches: 2.75, heightInches: 2 },
];

const squarePatchShapes: PatchShape[] = [
  { id: 'square-large', width: 157, height: 157, widthInches: 2.5, heightInches: 2.5 },
  { id: 'square-small', width: 126, height: 126, widthInches: 2, heightInches: 2 },
];

const circlePatchShapes: PatchShape[] = [
  { id: 'circle-large', width: 157, height: 157, widthInches: 2.5 },
  { id: 'circle-small', width: 126, height: 126, widthInches: 2 },
];

const outerStroke = {
  fill: 'none',
  stroke: '#fff',
  strokeWidth: 4,
};

const innerStroke = {
  fill: 'none',
  opacity: 0.5,
  stroke: '#fff',
  strokeDasharray: '12 9',
  strokeLinecap: 'round' as const,
  strokeWidth: 4,
};

interface Props {
  ariaLabel: string
}

interface SizeLabelProps {
  sizes: number[]
  x: number
  y: number
  textAnchor?: 'middle' | 'start'
}

function SizeLabel(props: SizeLabelProps) {
  return <text
    fill="#fff"
    fontFamily="Poppins, sans-serif"
    fontSize="18"
    fontWeight="600"
    textAnchor={props.textAnchor ?? 'middle'}
    x={props.x}
    y={props.y}
  >
    <SizeDisplay sizes={props.sizes} sourceUnit="in" displayType="physical" />
  </text>;
}

interface PatchOutlineProps extends PatchShape {
  type: PatchShapeType
  x: number
  y: number
}

function PatchOutline(props: PatchOutlineProps) {
  const inset = props.type === 'circle' || props.type === 'oval' ? 7 : 9;

  if (props.type === 'oval' || props.type === 'circle') {
    return <>
      <ellipse
        {...outerStroke}
        cx={props.x + props.width / 2}
        cy={props.y + props.height / 2}
        rx={props.width / 2}
        ry={props.height / 2}
      />
      <ellipse
        {...innerStroke}
        cx={props.x + props.width / 2}
        cy={props.y + props.height / 2}
        rx={props.width / 2 - inset}
        ry={props.height / 2 - inset}
      />
    </>;
  }

  const radius = props.type === 'square' ? 18 : 24;

  return <>
    <rect
      {...outerStroke}
      height={props.height}
      rx={radius}
      width={props.width}
      x={props.x}
      y={props.y}
    />
    <rect
      {...innerStroke}
      height={props.height - inset * 2}
      rx={Math.max(radius - inset, 4)}
      width={props.width - inset * 2}
      x={props.x + inset}
      y={props.y + inset}
    />
  </>;
}

function DimensionGuide(props: PatchOutlineProps) {
  const widthGuideY = props.y - 28;
  const heightGuideX = props.x + props.width + 22;

  return <>
    <line
      stroke="#fff"
      strokeDasharray="7 8"
      strokeWidth="1.5"
      x1={props.x}
      x2={props.x + props.width}
      y1={widthGuideY}
      y2={widthGuideY}
    />
    <SizeLabel
      sizes={[props.widthInches]}
      x={props.x + props.width / 2}
      y={widthGuideY - 10}
    />

    {props.heightInches !== undefined ? <>
      <line
        stroke="#fff"
        strokeDasharray="7 8"
        strokeWidth="1.5"
        x1={heightGuideX}
        x2={heightGuideX}
        y1={props.y}
        y2={props.y + props.height}
      />
      <SizeLabel
        sizes={[props.heightInches]}
        textAnchor="start"
        x={heightGuideX + 11}
        y={props.y + props.height / 2 + 6}
      />
    </> : null}
  </>;
}

interface PatchShapeVariantDiagramProps {
  shape: PatchShape
  type: PatchShapeType
}

function PatchShapeVariantDiagram(props: PatchShapeVariantDiagramProps) {
  const shapeAreaWidth = props.shape.heightInches === undefined ? diagramWidth : labeledShapeAreaWidth;
  const x = (shapeAreaWidth - props.shape.width) / 2;
  const y = diagramTopPadding;

  return <Diagram
    aria-hidden
    focusable={false}
    viewBox={`0 0 ${diagramWidth} ${y + props.shape.height + diagramBottomPadding}`}
  >
    <DimensionGuide {...props.shape} type={props.type} x={x} y={y} />
    <PatchOutline {...props.shape} type={props.type} x={x} y={y} />
  </Diagram>;
}

function PatchShapeDiagram(props: PatchShapeGroup) {
  return <ShapeGroupGrid container rowSpacing={{
    xs: 4,
    md: 5,
  }}>
    {props.variants.map(shape => <Grid key={shape.id} item xs={12}>
      <PatchShapeVariantDiagram shape={shape} type={props.type} />
    </Grid>)}
  </ShapeGroupGrid>;
}

function PrintedPatchMeasurementDiagram(props: Props) {
  return <Grid
    container
    role="img"
    aria-label={props.ariaLabel}
    columnSpacing={{
      xs: 3,
      sm: 4,
      md: 5,
    }}
    rowSpacing={{
      xs: 6,
      sm: 5,
      lg: 0,
    }}
    justifyContent="center"
  >
    <ShapeGroupGridItem item xs={12} sm={6} lg={3}>
      <PatchShapeDiagram id="oval" type="oval" variants={ovalPatchShapes} />
    </ShapeGroupGridItem>
    <ShapeGroupGridItem item xs={12} sm={6} lg={3}>
      <PatchShapeDiagram id="rectangle" type="rectangle" variants={rectanglePatchShapes} />
    </ShapeGroupGridItem>
    <ShapeGroupGridItem item xs={12} sm={6} lg={3}>
      <PatchShapeDiagram id="square" type="square" variants={squarePatchShapes} />
    </ShapeGroupGridItem>
    <ShapeGroupGridItem item xs={12} sm={6} lg={3}>
      <PatchShapeDiagram id="circle" type="circle" variants={circlePatchShapes} />
    </ShapeGroupGridItem>
  </Grid>
}

export default memo(PrintedPatchMeasurementDiagram);
